import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { money } from '../form';
import { errorText, useI18n } from '../i18n';
import { Alert, AuthCard } from '../layout';

interface PayView {
  invoice: { label: string; seller: string; buyer: string; issueDate: string; dueDate: string | null; total: number; left: number; currency: string; paid: boolean };
  methods: { stripe: boolean; paysera?: boolean; swedbank?: boolean };
}

/** The page an invoice's payment link opens (no sign-in): what is left to pay and how to pay it online. */
export function PayPage({ token, returned }: { token: string; returned: boolean }) {
  const { t } = useI18n();
  const lookup = useQuery({ queryKey: ['pay', token], enabled: Boolean(token), retry: false, refetchInterval: (q) => (returned && !q.state.data?.invoice.paid ? 3000 : false),
    queryFn: () => api.post<PayView>('api/v1/pay/lookup', { token }) });
  const card = useMutation({
    mutationFn: () => api.post<{ url: string }>('api/v1/pay/stripe', { token }),
    onSuccess: (r) => { window.location.assign(r.url); },
  });
  const swed = useMutation({
    mutationFn: () => api.post<{ url: string }>('api/v1/pay/swedbank', { token }),
    onSuccess: (r) => { window.location.assign(r.url); },
  });
  const bank = useMutation({
    mutationFn: () => api.post<{ url: string }>('api/v1/pay/paysera', { token }),
    onSuccess: (r) => { window.location.assign(r.url); },
  });
  const download = async () => {
    const r = await fetch('api/v1/pay/pdf', { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json', 'x-fsp-request': '1' }, body: JSON.stringify({ token }) });
    if (!r.ok) return;
    const url = URL.createObjectURL(await r.blob());
    const link = document.createElement('a');
    link.href = url;
    link.download = `${lookup.data?.invoice.label ?? 'saskaita'}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  };
  const v = lookup.data;
  const i = v?.invoice;
  return (
    <AuthCard eyebrow={i?.seller ?? 'FakturaSmart Pro'} title={i ? t('Sąskaita {n}', { n: i.label }) : t('Sąskaitos apmokėjimas')}>
      {lookup.isError ? <Alert kind="error">{errorText(t, lookup.error)}</Alert> : null}
      {i ? <>
        <dl className="pay-summary">
          <div><dt>{t('Pardavėjas')}</dt><dd>{i.seller}</dd></div>
          <div><dt>{t('Pirkėjas')}</dt><dd>{i.buyer}</dd></div>
          <div><dt>{t('Data')}</dt><dd>{i.issueDate}</dd></div>
          {i.dueDate ? <div><dt>{t('Apmokėti iki')}</dt><dd>{i.dueDate}</dd></div> : null}
          <div><dt>{t('Suma')}</dt><dd>{money(i.total, i.currency)}</dd></div>
          {!i.paid ? <div className="pay-left"><dt>{t('Liko sumokėti')}</dt><dd>{money(i.left, i.currency)}</dd></div> : null}
        </dl>
        <button type="button" className="text-link" onClick={() => void download()}>{t('Atsisiųsti PDF')}</button>
        {i.paid ? <Alert kind="success">{t('Sąskaita apmokėta. Ačiū!')}</Alert>
          : returned ? <Alert kind="success">{t('Mokėjimas gautas, tikriname… Puslapis atsinaujins pats.')}</Alert>
          : v.methods.stripe || v.methods.paysera || v.methods.swedbank ? (
            <div className="pay-methods">
              {card.isError || bank.isError || swed.isError ? <Alert kind="error">{errorText(t, card.error ?? bank.error ?? swed.error)}</Alert> : null}
              {v.methods.swedbank && i.currency === 'EUR' ? <>
                <button type="button" className="button button--primary" disabled={swed.isPending} onClick={() => swed.mutate()}>{t('Apmokėti per Swedbank')} · {money(i.left, i.currency)}</button>
                <p className="muted">{t('Mokėjimą patvirtinsite savo Swedbank internetiniame banke.')}</p>
              </> : null}
              {v.methods.paysera ? <>
                <button type="button" className={`button ${v.methods.swedbank ? 'button--ghost' : 'button--primary'}`} disabled={bank.isPending} onClick={() => bank.mutate()}>{t('Apmokėti per banką')} · {money(i.left, i.currency)}</button>
                <p className="muted">{t('Swedbank, SEB, Luminor ir kiti bankai – saugiame Paysera puslapyje.')}</p>
              </> : null}
              {v.methods.stripe ? <>
                <button type="button" className={`button ${v.methods.paysera ? 'button--ghost' : 'button--primary'}`} disabled={card.isPending} onClick={() => card.mutate()}>{t('Apmokėti kortele')} · {money(i.left, i.currency)}</button>
                <p className="muted">{t('Mokėjimas vyksta saugiame Stripe puslapyje; kortelės duomenų mes nematome.')}</p>
              </> : null}
            </div>
          ) : <p className="muted">{t('Apmokėjimas internetu šiai sąskaitai neįjungtas. Apmokėkite pavedimu pagal sąskaitos rekvizitus.')}</p>}
      </> : null}
    </AuthCard>
  );
}
